/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.sprudel.ui

import io.peekandpoke.klang.ui.HoverPopupCtrl
import io.peekandpoke.klang.ui.KlangUiToolContext
import io.peekandpoke.klang.ui.KlangUiToolEmbeddable
import io.peekandpoke.klang.ui.codetools.KlangToolAutoUpdate
import io.peekandpoke.klang.ui.feel.KlangTheme
import io.peekandpoke.klang.ui.svgLine
import io.peekandpoke.klang.ui.svgPath
import io.peekandpoke.klang.ui.svgRect
import io.peekandpoke.klang.ui.svgRoot
import io.peekandpoke.klang.ui.svgText
import io.peekandpoke.kraft.components.Component
import io.peekandpoke.kraft.components.Ctx
import io.peekandpoke.kraft.components.comp
import io.peekandpoke.kraft.forms.formController
import io.peekandpoke.kraft.popups.PopupsManager.Companion.popups
import io.peekandpoke.kraft.semanticui.forms.UiInputField
import io.peekandpoke.kraft.vdom.VDom
import io.peekandpoke.ultra.common.toFixed
import io.peekandpoke.ultra.html.css
import io.peekandpoke.ultra.html.key
import io.peekandpoke.ultra.html.onClick
import io.peekandpoke.ultra.semanticui.SemanticIconFn
import io.peekandpoke.ultra.semanticui.ui
import kotlinx.css.Display
import kotlinx.css.FlexWrap
import kotlinx.css.WhiteSpace
import kotlinx.css.display
import kotlinx.css.flexWrap
import kotlinx.css.gap
import kotlinx.css.marginBottom
import kotlinx.css.minWidth
import kotlinx.css.px
import kotlinx.css.rem
import kotlinx.css.whiteSpace
import kotlinx.html.FlowContent
import kotlinx.html.Tag
import kotlinx.html.div
import kotlin.math.max

// ── Tool singleton ───────────────────────────────────────────────────────────

/**
 * [KlangUiToolEmbeddable] for the per-param compressor(threshold, ratio, knee, attack, release) call.
 *
 * Two modes (C0.3 two-tool-tier design):
 * - Whole-call modal: when [KlangUiToolContext.call] is present, edits all five params of the
 *   host call (incl. presets) and commits the full argument list.
 * - Scalar fallback (embedded / sequence atom): edits a single threshold value in dB.
 */
object SprudelCompressorEditorTool : KlangUiToolEmbeddable {
    override val title: String = "Compressor"

    override val iconFn: SemanticIconFn = { compress }

    override fun FlowContent.render(ctx: KlangUiToolContext) {
        SprudelCompressorEditorComp(ctx, embedded = false)
    }

    override fun FlowContent.renderEmbedded(ctx: KlangUiToolContext) {
        SprudelCompressorEditorComp(ctx, embedded = true)
    }
}

// ── Entry-point helper ───────────────────────────────────────────────────────

@Suppress("FunctionName")
private fun Tag.SprudelCompressorEditorComp(toolCtx: KlangUiToolContext, embedded: Boolean) =
    comp(SprudelCompressorEditorComp.Props(toolCtx, embedded)) { SprudelCompressorEditorComp(it) }

// ── Presets ──────────────────────────────────────────────────────────────────

private data class CompressorPreset(
    val name: String,
    val threshold: Double,
    val ratio: Double,
    val knee: Double,
    val attack: Double,
    val release: Double,
)

private val PRESETS = listOf(
    CompressorPreset("Gentle Leveling", -15.0, 2.0, 6.0, 0.01, 0.2),
    CompressorPreset("Punchy Drums", -20.0, 4.0, 3.0, 0.03, 0.1),
    CompressorPreset("Tight Percussion", -18.0, 6.0, 1.0, 0.005, 0.05),
    CompressorPreset("Vocal Smoothing", -12.0, 3.0, 8.0, 0.01, 0.15),
    CompressorPreset("Heavy Squeeze", -30.0, 8.0, 2.0, 0.005, 0.1),
    CompressorPreset("Sidechain Pump", -25.0, 10.0, 0.0, 0.001, 0.3),
    CompressorPreset("Brickwall Limiter", -2.0, 40.0, 0.0, 0.001, 0.05),
    CompressorPreset("Transparent Glue", -10.0, 2.0, 10.0, 0.02, 0.25),
)

// ── Component ────────────────────────────────────────────────────────────────

private class SprudelCompressorEditorComp(ctx: Ctx<Props>) : Component<SprudelCompressorEditorComp.Props>(ctx) {

    data class Props(val toolCtx: KlangUiToolContext, val embedded: Boolean = false)

    private val laf by subscribingTo(KlangTheme)
    private val autoUpdate by subscribingTo(KlangToolAutoUpdate)

    private val infoPopup = HoverPopupCtrl(popups)

    private val formCtrl = formController()

    private val call = props.toolCtx.call

    private val initialValue = props.toolCtx.currentValue ?: ""
    private var currentValue by value(initialValue)

    // Whole-call mode reads the params from the host call's args; scalar mode reads the single arg.
    private val parsedThreshold
        get() = parseNum(call?.args?.getOrNull(0) ?: currentValue, -20.0)

    private val parsedRatio
        get() = parseNum(call?.args?.getOrNull(1), 4.0)

    private val parsedKnee
        get() = parseNum(call?.args?.getOrNull(2), 6.0)

    private val parsedAttack
        get() = parseNum(call?.args?.getOrNull(3), 0.003)

    private val parsedRelease
        get() = parseNum(call?.args?.getOrNull(4), 0.1)

    private var threshold by value(parsedThreshold)
    private var ratio by value(parsedRatio)
    private var knee by value(parsedKnee)
    private var attack by value(parsedAttack)
    private var release by value(parsedRelease)

    // Slot bookkeeping: an untouched arg that fails the parse (pattern, variable, expression)
    // must never be overwritten, and untouched absent slots stay absent (engine defaults apply).
    private val parseable: List<Boolean> = List(5) { parseNumOrNull(call?.args?.getOrNull(it)) != null }
    private val dirty = mutableSetOf<Int>()
    private var hasCommitted = false

    private var resetCounter by value(0)

    // ── Helpers ──────────────────────────────────────────────────────────────

    private fun buildValue(): String =
        if (call != null) {
            "${threshold.formatArg()}, ${ratio.formatArg()}, ${knee.formatArg()}, ${attack.formatArg()}, ${release.formatArg()}"
        } else {
            threshold.formatArg()
        }

    /**
     * Writes a slot only when that is safe: the user touched it, or the original arg parses
     * (rewriting it loses nothing). Untouched non-parseable args are preserved; untouched
     * absent slots stay absent so the engine defaults apply.
     */
    private fun put(texts: MutableList<String?>, index: Int, text: String?) {
        val original = call?.args?.getOrNull(index)
        if (index in dirty || (original != null && parseable[index])) {
            texts[index] = text
        }
    }

    private fun commitValue() {
        val c = call
        if (c != null) {
            val texts = c.args.toMutableList()
            while (texts.size < 5) texts.add(null)
            put(texts, 0, threshold.formatArg())
            put(texts, 1, ratio.formatArg())
            put(texts, 2, knee.formatArg())
            put(texts, 3, attack.formatArg())
            put(texts, 4, release.formatArg())
            c.onCommitCall(texts)
        } else {
            props.toolCtx.onCommit(threshold.formatArg())
        }
        hasCommitted = true
        lastCommitted = buildValue()
    }

    // Built-state fingerprints: in whole-call mode [initialValue] is only the clicked arg's
    // text, so the Reset/Update buttons compare built snapshots instead (initial state and
    // last committed state); scalar mode keeps the plain text comparison.
    private val initialBuiltValue = buildValue()
    private var lastCommitted = initialBuiltValue

    private val isInitialModified
        get() = if (call != null) buildValue() != initialBuiltValue else initialValue != buildValue()

    private val isCurrentModified
        get() = if (call != null) buildValue() != lastCommitted else currentValue != buildValue()

    private fun liveUpdate() {
        if (props.embedded || autoUpdate) {
            commitValue()
        }
    }

    private fun onCancel() {
        if (!props.embedded && autoUpdate && hasCommitted && isInitialModified) {
            val c = call
            if (c != null) c.onCommitCall(c.args) else props.toolCtx.onCommit(initialValue)
        }
        props.toolCtx.onCancel()
    }

    private fun onReset() {
        dirty.clear()
        currentValue = initialValue
        threshold = parsedThreshold
        ratio = parsedRatio
        knee = parsedKnee
        attack = parsedAttack
        release = parsedRelease
        formCtrl.resetAllFields()
        commitValue()
        resetCounter++
    }

    private fun onCommit() {
        currentValue = buildValue()
        commitValue()
    }

    private fun applyPreset(preset: CompressorPreset) {
        threshold = preset.threshold
        ratio = preset.ratio
        knee = preset.knee
        attack = preset.attack
        release = preset.release
        dirty += 0..4
        formCtrl.resetAllFields()
        resetCounter++
        liveUpdate()
    }

    // ── Render ───────────────────────────────────────────────────────────────

    override fun VDom.render() {
        if (props.embedded) {
            renderContent()
        } else {
            ui.segment {
                key = "compressor-editor"
                css { minWidth = 560.px }
                toolHeaderWithInfo("Compressor", props.toolCtx, infoPopup)
                renderContent()
                ui.divider {}
                ToolButtonBar(
                    isInitialModified = isInitialModified,
                    isCurrentModified = isCurrentModified,
                    onCancel = ::onCancel,
                    onReset = ::onReset,
                    onCommit = ::onCommit,
                )
            }
        }
    }

    private fun FlowContent.renderContent() {
        div {
            key = "compressor-editor-content-$resetCounter"

            // Presets set all five params, so they only make sense in whole-call mode
            if (call != null) {
                div {
                    key = "compressor-presets"
                    css {
                        display = Display.flex
                        flexWrap = FlexWrap.wrap
                        gap = 4.px
                        marginBottom = 8.px
                    }
                    val matchedPreset = PRESETS.find {
                        it.threshold == threshold && it.ratio == ratio && it.knee == knee &&
                                it.attack == attack && it.release == release
                    }

                    for (preset in PRESETS) {
                        val isSelected = preset === matchedPreset
                        ui.mini.givenNot(isSelected) { basic }.given(isSelected) { with(laf.styles.goldButton()) }.button {
                            css { whiteSpace = WhiteSpace.nowrap }
                            onClick { applyPreset(preset) }
                            +preset.name
                        }
                    }

                    val isCustom = matchedPreset == null
                    ui.mini.givenNot(isCustom) { basic }.given(isCustom) { with(laf.styles.goldButton()) }.button {
                        css { whiteSpace = WhiteSpace.nowrap }
                        +"Custom"
                    }
                }

                ui.divider()
            }

            ui.form {
                key = "compressor-editor-form"
                ui.five.stackable.fields {
                    key = "compressor-editor-fields"
                    UiInputField(threshold, { threshold = it; dirty += 0; liveUpdate() }) {
                        domKey("threshold")
                        step(1.0)
                        label {
                            +"Threshold"
                            paramInfoIcon("threshold", props.toolCtx, infoPopup)
                        }
                        rightLabel { ui.basic.label { +"dB" } }
                    }
                    if (call != null) {
                        UiInputField(ratio, { ratio = it; dirty += 1; liveUpdate() }) {
                            domKey("ratio")
                            step(0.5)
                            label {
                                +"Ratio"
                                paramInfoIcon("ratio", props.toolCtx, infoPopup)
                            }
                        }
                        UiInputField(knee, { knee = it; dirty += 2; liveUpdate() }) {
                            domKey("knee")
                            step(0.5)
                            label {
                                +"Knee"
                                paramInfoIcon("knee", props.toolCtx, infoPopup)
                            }
                            rightLabel { ui.basic.label { +"dB" } }
                        }
                        UiInputField(attack, { attack = it; dirty += 3; liveUpdate() }) {
                            domKey("attack")
                            step(0.001)
                            label {
                                +"Attack"
                                paramInfoIcon("attack", props.toolCtx, infoPopup)
                            }
                            rightLabel { ui.basic.label { +"sec" } }
                        }
                        UiInputField(release, { release = it; dirty += 4; liveUpdate() }) {
                            domKey("release")
                            step(0.01)
                            label {
                                +"Release"
                                paramInfoIcon("release", props.toolCtx, infoPopup)
                            }
                            rightLabel { ui.basic.label { +"sec" } }
                        }
                    }
                }
            }
            ui.divider {
                key = "compressor-editor-divider"
            }
            div {
                key = "compressor-editor-curve"
                css { if (!props.embedded) marginBottom = 1.rem }
                renderCompressorSvg()
            }
        }
    }

    // ── SVG visualization ────────────────────────────────────────────────────

    private fun FlowContent.renderCompressorSvg() {
        val w = 560.0
        val h = 140.0
        val padL = 32.0
        val padR = 12.0
        val padT = 10.0
        val padB = 22.0
        val drawW = w - padL - padR
        val drawH = h - padT - padB

        // dB range: -60 to 0
        val dbMin = -60.0
        val dbMax = 0.0
        val dbRange = dbMax - dbMin

        fun dbToX(db: Double) = padL + ((db - dbMin) / dbRange) * drawW
        fun dbToY(db: Double) = padT + drawH - ((db - dbMin) / dbRange) * drawH

        // Compute the compressor transfer function
        val safeThreshold = threshold.coerceIn(-60.0, 0.0)
        val safeRatio = max(ratio, 1.0)
        val safeKnee = max(knee, 0.0)
        val halfKnee = safeKnee / 2.0

        fun outputDb(inputDb: Double): Double {
            return if (safeKnee <= 0.0) {
                // Hard knee
                if (inputDb <= safeThreshold) {
                    inputDb
                } else {
                    safeThreshold + (inputDb - safeThreshold) / safeRatio
                }
            } else {
                // Soft knee
                val kneeStart = safeThreshold - halfKnee
                val kneeEnd = safeThreshold + halfKnee
                when {
                    inputDb <= kneeStart -> inputDb
                    inputDb >= kneeEnd -> safeThreshold + (inputDb - safeThreshold) / safeRatio
                    else -> {
                        // Quadratic interpolation in the knee region
                        val x = inputDb - kneeStart
                        val kneeWidth = safeKnee
                        inputDb + ((1.0 / safeRatio - 1.0) * (x * x)) / (2.0 * kneeWidth)
                    }
                }
            }
        }

        // Build polyline points
        val step = 1.0
        val points = buildString {
            var db = dbMin
            var first = true
            while (db <= dbMax) {
                val outDb = outputDb(db).coerceIn(dbMin, dbMax)
                val x = dbToX(db)
                val y = dbToY(outDb)
                if (!first) append(" ")
                append("${x.toFixed(1)},${y.toFixed(1)}")
                first = false
                db += step
            }
        }

        // Fill path
        val fillPath = buildString {
            // Start at bottom-left
            append("M${dbToX(dbMin).toFixed(1)} ${dbToY(dbMin).toFixed(1)} ")
            var db = dbMin
            while (db <= dbMax) {
                val outDb = outputDb(db).coerceIn(dbMin, dbMax)
                append("L${dbToX(db).toFixed(1)} ${dbToY(outDb).toFixed(1)} ")
                db += step
            }
            // Close: go right to the unity line and back
            append("L${dbToX(dbMax).toFixed(1)} ${dbToY(dbMin).toFixed(1)} Z")
        }

        // Threshold marker
        val threshX = dbToX(safeThreshold)

        // Grid positions
        val gridDbs = listOf(-48.0, -36.0, -24.0, -12.0)

        svgRoot(viewBox = "0 0 $w $h") {
            // Background
            svgRect(padL, padT, drawW, drawH, fill = "rgba(0,0,0,0.2)", rx = "2")

            // Unity line (1:1 diagonal)
            svgLine(
                dbToX(dbMin), dbToY(dbMin),
                dbToX(dbMax), dbToY(dbMax),
                stroke = "rgba(255,255,255,0.1)", strokeWidth = "0.5",
            )

            // Grid lines
            for (db in gridDbs) {
                // Vertical
                svgLine(dbToX(db), padT, dbToX(db), padT + drawH, stroke = "rgba(255,255,255,0.08)", strokeWidth = "0.5")
                // Horizontal
                svgLine(padL, dbToY(db), padL + drawW, dbToY(db), stroke = "rgba(255,255,255,0.08)", strokeWidth = "0.5")
            }

            // Threshold line
            svgLine(threshX, padT, threshX, padT + drawH, stroke = "rgba(255,255,255,0.25)", strokeWidth = "0.5")

            // Fill under curve
            svgPath(d = fillPath, fill = "${laf.gold}26")

            // Compressor curve
            svgPath(
                d = "M${points.replace(" ", "L").replace(",", " ")}",
                stroke = laf.gold,
                strokeWidth = "1",
            )

            // Axis labels
            svgText(padL - 4, dbToY(0.0), "0", fill = "#ccc", fontSize = "7", textAnchor = "end")
            svgText(padL - 4, dbToY(-24.0), "-24", fill = "#ccc", fontSize = "7", textAnchor = "end")
            svgText(padL - 4, dbToY(-48.0), "-48", fill = "#ccc", fontSize = "7", textAnchor = "end")

            svgText(dbToX(0.0), h - 5, "0 dB", fill = "#ccc", fontSize = "7", textAnchor = "middle")
            svgText(dbToX(-24.0), h - 5, "-24", fill = "#ccc", fontSize = "7", textAnchor = "middle")
            svgText(dbToX(-48.0), h - 5, "-48", fill = "#ccc", fontSize = "7", textAnchor = "middle")

            // Threshold label
            svgText(threshX, padT - 2, "T", fill = "#ccc", fontSize = "7", textAnchor = "middle")
        }
    }
}
